import { useState, useRef, useEffect, useCallback } from 'react';
import { Dialog } from '@headlessui/react';
import { ProductModalProps, ProductModalStateProps } from '../../types/components/collections/ProductModal';
import { User } from '../../types/api/UsersTypes';
import { SubscriptionDetail } from '../../types/api/SubscriptionTypes';
import {
  DownloadIcon,
  LeftCaretIcon,
  RightCaretIcon,
  ExitIcon,
  DownChevronIcon,
  UnlockIcon,
  EssentialsLogo
} from '../../modules/Icons';
import { connect } from 'react-redux';
import AddToWishlistButton from '../wishlists/AddToWishlistButton';
import { ApplicationState } from '../../types/state/storeTypes';
import {
  fixOptionTitle,
  getVideoSources,
  optionablesToSpecs
} from '../../helpers/pages/collections/collectionsHelpers';
import resolveProductModal from '../../hooks/resolveProductModal';
import { Loader } from '../shared/Loader';
import CopyToClipboardButton from '../shared/CopyToClipboardButton';
import PriceElement from '../pricing/PriceElement';
import DownloadAndPurchaseButtonBlock from '../products/DownloadAndPurchaseButtonBlock';
import { ProductOwnershipResponse } from '../../types/api/OwnershipTypes';
import LicenseConfirmationFlow from '../license-confirmation-modals/LicenseConfirmationFlow';
import { isCreditBasedLegacy, isLegacy } from '../../helpers/subscriptionHelpers';
import LegacyProductCreditsPurchaseBlock from './LegacyProductCreditsPurchaseBlock';
import purchaseHelpers from '../../helpers/purchaseHelpers';
import MobileSwiper from '../helpers/MobileSwiper';
import SwipeHint from '../helpers/SwipeHint';
import isMobile from '../../hooks/isMobile';
import useVideoOrientation from '../../hooks/useVideoOrientation';
import algoliaHelpers from '../../helpers/algolia';
import EssentialsMonthlyExpiryDate from '../shared/EssentialsMonthlyExpiryDate';
import { useRouter } from 'next/router';
import { QUERY_PARAMS as ALGOLIA_QUERY_PARAMS } from '../../constants/algolia';

const ProductModal: React.FC<ProductModalProps> = (props) => {
  let {
    product,
    collection,
    closeCallback,
    showArrows,
    ownership: _ownership,
    nextProduct,
    previousProduct,
    setSelectedProduct,
    onOwnershipDataLoaded,
    subscription,
    user,
    authenticated,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [productOwned, setProductOwned] = useState(!!_ownership);
  const [ownership, setOwnership] = useState(_ownership);
  const [isConfirmLicenseFlowOpen, setIsConfirmLicenseFlowOpen] = useState(false);
  const [isEndOfCollection, setIsEndOfCollection] = useState(false);
  const [playHintAnimation, setPlayHintAnimation] = useState(true);
  const [disableDownloadButton, setDisableDownloadButton] = useState<boolean>(false);
  const [showEndIndicator, setShowEndIndicator] = useState(false);

  const { determineNeedsRepurchase } = purchaseHelpers;
  const { pushClipClickEvent } = algoliaHelpers;
  const router = useRouter();

  useEffect(() => {
    setIsVideoLoading(true);
    setIsEndOfCollection(!nextProduct);
  }, [product]);

  useEffect(() => {
    const hasNonLegitimateLicense = determineNeedsRepurchase(ownership, collection);
    setDisableDownloadButton(hasNonLegitimateLicense);
  }, [ownership, collection]);

  const sendAlgoliaEvent = async () => {
    let position: number;
    if (router.query[ALGOLIA_QUERY_PARAMS.POSITION])
      position = parseInt(router.query[ALGOLIA_QUERY_PARAMS.POSITION] as string);
    await pushClipClickEvent(user, collection.id, router.query[ALGOLIA_QUERY_PARAMS.QUERY_ID] as string, position);
  };

  useEffect(() => {
    if (authenticated !== true && authenticated !== false) return;
    sendAlgoliaEvent();
  }, [collection, authenticated, product])

  const handleLicenseFlowClose = () => {
    setIsConfirmLicenseFlowOpen(false);
  };

  useEffect(() => {
    setOwnership(_ownership);
    setProductOwned(!!_ownership);
  }, [_ownership]);

  const handleOwnershipDataLoaded = (ownershipData: ProductOwnershipResponse) => {
    setOwnership(ownershipData);
    if (onOwnershipDataLoaded) onOwnershipDataLoaded(ownershipData);
  };

  const videoRef = useRef<HTMLVideoElement>(null);
  const isMobileDevice = isMobile();
  const isVideoLandscape = useVideoOrientation(videoRef);

  const resolvedModalData = !collection && !ownership ? resolveProductModal(product) : null;
  if (resolvedModalData) {
    collection = resolvedModalData.collection;
    setOwnership(resolvedModalData.ownership);
  }

  function hasJustUnoBanner(): boolean {
    return !!document.getElementsByClassName('ju_Con').length;
  }

  function getTopMarginClass(): string {
    if (!hasJustUnoBanner()) return 'mt-0';
    return 'mt-[140px]';
  }

  const specs = optionablesToSpecs(product?.variants);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const displayedSpecs = isExpanded ? Object.entries(specs) : Object.entries(specs).slice(0, 4);

  if (product == null) return;
  const buttonClasses =
    'rounded-[5px] border border-gray-800 px-5 py-1 flex align-middle items-center gap-2 text-[13px] justify-center';
  const hoverClasses = 'hover:text-white';

  // const {
  //   loading: loadingVariantPrices,
  //   data: variantPrices,
  //   error: loadingVariantPricesError,
  //   fetchFunction: loadVariantPrices
  // } = getProductPrices(product);

  // function reloadVariantPrices(): void {
  //   loadVariantPrices(product);
  // }

  useEffect(() => {
    setIsOpen(true);
  }, [product]);

  useEffect(() => {
    videoRef.current?.load();
    if (videoRef && videoRef.current) {
      videoRef.current.play();
    }
  }, [videoRef, product]);

  function closeModal() {
    setIsOpen(false);
    closeCallback();
  }

  function setNextProduct() {
    if (!showArrows || !nextProduct) return;
    setSelectedProduct(nextProduct);
  }

  function setPreviousProduct() {
    if (!showArrows || !previousProduct) return;
    setSelectedProduct(previousProduct);
  }

  function handleModalKeyDown(event: any) {
    switch (event.key) {
      case 'ArrowRight':
        setNextProduct();
        break;
      case 'ArrowLeft':
        setPreviousProduct();
        break;
      default:
        break;
    }
  }

  function isLoading(): boolean {
    return resolvedModalData?.loadingOwnership || resolvedModalData?.loadingCollection;
  }

  const containerClass = (isVideoLandscape ? 'h-full xl:max-h-[550px]' : 'h-[600px]')

  const handleSwipe = useCallback(({ deltaX }) => {
    // If trying to go next but no next product
    if (deltaX < 0 && !nextProduct) {
      setShowEndIndicator(true);
      // Hide indicator after animation
      setTimeout(() => setShowEndIndicator(false), 500);
      return;
    }
    // If trying to go previous but no previous product
    if (deltaX > 0 && !previousProduct) {
      setShowEndIndicator(true);
      setTimeout(() => setShowEndIndicator(false), 500);
      return;
    }

    if (deltaX > 0 && previousProduct) {
      setPreviousProduct();
    } else if (deltaX < 0 && nextProduct) {
      setNextProduct();
    }
  }, [previousProduct, nextProduct, setPreviousProduct, setNextProduct]);

  return (
    <>
      {isLoading() && <Loader />}
      <LicenseConfirmationFlow open={isConfirmLicenseFlowOpen} onClose={handleLicenseFlowClose} isUpdate={false} />
      {!isLoading() && !!collection && (
        <Dialog open={isOpen} onClose={() => closeModal()} className="relative z-40 w-full">
          <div className="fixed inset-0 bg-black/80" aria-hidden="true" key={product.id} />
          <div
            className="fixed inset-0 overflow-x-hidden overflow-y-auto"
            tabIndex={0}
            onKeyDown={(e) => handleModalKeyDown(e)}>
            <div className="flex items-center justify-center min-h-full ">
              <Dialog.Panel
                className={`relative mx-auto w-screen md:w-max xl:w-8/12 max-w-[1000px] rounded-[5px] bg-a-dark-gray text-white ${getTopMarginClass()}`}>
                <div
                  className="absolute z-10 cursor-pointer top-5 right-5"
                  onClick={() => closeModal()}>
                  <ExitIcon />
                </div>

                <MobileSwiper onSwipe={handleSwipe}>
                  <div className={`absolute top-0 right-0 h-full w-1 bg-red-500 z-[99] transition-opacity duration-300 ${showEndIndicator && !nextProduct ? 'opacity-50' : 'opacity-0'}`} />
                  <div className={`absolute top-0 left-0 h-full w-1 bg-red-500 z-[99] transition-opacity duration-300 ${showEndIndicator && !previousProduct ? 'opacity-50' : 'opacity-0'}`} />
                  <div className={`relative w-full ${containerClass} transition-all duration-300 ${(!localStorage.getItem('hasUserSwiped') && playHintAnimation && isMobileDevice) ? isEndOfCollection ? 'animate-container-slide-left' : 'animate-container-slide-right' : 'none'}`}
                  >
                    {isMobileDevice && <SwipeHint show={!!nextProduct || !!previousProduct} onSwipe={() => console.log("swiped!")} switchDirection={isEndOfCollection} />}
                    {isVideoLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black">
                        <div className="w-8 h-8 border-2 rounded-full border-a-blue border-t-transparent animate-spin"></div>
                      </div>
                    )}
                    <video
                      className="w-full object-contain max-h-[600px]"
                      preload="none"
                      poster={product.video?.poster || product.media?.image}
                      ref={videoRef}
                      controls
                      loop
                      autoPlay
                      playsInline
                      muted
                      onLoadedData={() => setIsVideoLoading(false)}
                      onPlay={(e) => {
                        // Prevent fullscreen on play
                        if (document.fullscreenElement) {
                          document.exitFullscreen();
                        }
                      }}
                    >
                      {getVideoSources(product).map((source, k) => (
                        <source key={'video_' + k} src={source.src} type={source.type} />
                      ))}
                    </video>
                  </div>
                </MobileSwiper>

                {showArrows && (
                  <div className="relative w-screen md:w-full">
                    {previousProduct && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviousProduct();
                        }}
                        className="w-[158px] absolute -left-[205px] -top-[30px] hidden xl:block cursor-pointer stroke-gray-600 text-gray-600 hover:stroke-white hover:text-white z-49 bg-white bg-opacity-10 rounded-full p-4 hover:shadow-xl">
                        <LeftCaretIcon className="inline mr-2" />
                        Previous Clip
                      </span>
                    )}
                    {nextProduct && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          setNextProduct();
                        }}
                        className="w-[129px] absolute -top-[30px] -right-[176px] hidden xl:block cursor-pointer stroke-gray-600 text-gray-600 hover:stroke-white hover:text-white z-49 bg-white bg-opacity-10 rounded-full p-4 hover:shadow-xl">
                        Next Clip <RightCaretIcon className="inline ml-2" />
                      </span>
                    )}
                  </div>
                )}

                <div className="p-6 md:pt-[40px] md:p-[40px] md:px-12 leading-loose md:flex gap-8">
                  <div className="md:w-7/12">
                    <p className="uppercase text-a-light-gray text-[10px] md:text-[13px]">
                      ASSET ID: {product.sku}
                    </p>
                    <h1 className="font-[Inter] text-[22px] md:text-[26px] font-semibold">{product.name}</h1>
                    <p
                      className="text-[12px] md:text-[16px] text-[#F5F5F5] font-light leading-normal whitespace-pre-line"
                      dangerouslySetInnerHTML={{ __html: product.description }}></p>
                    <div className="border-t border-[#2C2C2C] py-6 my-6 text-gray-500 text-sm leading-normal">
                      <div className="flex flex-col gap-2 mt-4">
                        {displayedSpecs.map((v) => (
                          <div className="flex gap-1" key={v[0]}>
                            <strong className="capitalize text-[14px] block whitespace-nowrap">
                              {fixOptionTitle(v[0])}:
                            </strong>
                            <div className="block text-[14px] font-normal">
                              {v[1].map((row, k) => (
                                <span key={row.value}>
                                  {k !== 0 && <span className="text-gray-600"> / </span>}
                                  {row.value}
                                  {row.disclaimers.length > 0 && (
                                    <span className="text-gray-600 text-[12px]">
                                      {' '}
                                      ({row.disclaimers.join(', ')})
                                    </span>
                                  )}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                        <button
                          onClick={toggleExpand}
                          className={`${Object.entries(specs).length <= 4 && 'hidden'
                            } flex !justify-start text-[#AEB6BA] text-[14px]`}>
                          {isExpanded ? (
                            <span>
                              Show less{' '}
                              <DownChevronIcon className="inline ml-1 stroke-[#AEB6BA] rotate-180" />
                            </span>
                          ) : (
                            <span>
                              Show more <DownChevronIcon className="inline ml-1 stroke-[#AEB6BA]" />
                            </span>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                  {(
                    <div className="md:w-5/12">
                      {!!subscription && !isLegacy(subscription) && collection.membership_tier === 'silver' && (
                        <EssentialsMonthlyExpiryDate expiryDate={collection.ec_schedule_end_at} />
                      )}
                      {(!isCreditBasedLegacy(subscription) || productOwned) &&
                        <>
                          {!productOwned && collection.membership_tier != 'silver' && (
                            <>
                              <div className="w-full mb-[26px] flex justify-center">
                                <PriceElement purchasedItem={product} />
                              </div>
                            </>
                          )}

                          {!productOwned && !subscription && collection.membership_tier !== 'silver' && !collection.free_product && (
                            <div className='flex justify-center items-center md:items-start gap-[16px] bg-[#24C1FF1F] w-full h-[80px] mb-[16px] px-[24px] py-[16px] rounded-[5px]'>
                              <div className="flex-shrink-0 w-[32px] h-[32px]">
                                <UnlockIcon className='w-full h-full' />
                              </div>
                            <span className='font-light text-[12px] md:text-[16px] md:leading-[24px]'>Unlock access to individual assets by subscribing today!</span>
                            </div>
                          )}

                          <DownloadAndPurchaseButtonBlock
                            mode="product-modal"
                            onPurchased={() => setProductOwned(true)}
                            downloads={ownership?.downloads || product?.variant_downloads}
                            productItem={product}
                            collectionId={collection.id}
                            onOwnershipDataLoaded={handleOwnershipDataLoaded}
                            setIsConfirmLicenseFlowOpen={setIsConfirmLicenseFlowOpen}
                            needsRepurchase={disableDownloadButton}
                          />

                          {!subscription && collection.membership_tier === 'silver' && (
                          <div className="min-h-[54px] bg-[#24C1FF1F] my-4 py-4 px-8 rounded-[5px] flex flex-col gap-2 items-start">
                              <div className="flex items-center justify-start w-full whitespace-nowrap">
                                <span className="uppercase text-white eurostile text-[16px] md:text-[18px] font-semibold tracking-wider leading-[20px] align-super mr-[-10px] sm:mr-0">
                                  included in
                                </span>
                                <EssentialsLogo className="scale-[90%] md:scale-[100%] ml-2 rounded-[5px]" />
                              </div>
                              <div className='text-[14px] font-light leading-[22px]'>Unlock access to individual assets by subscribing today!</div>
                            <EssentialsMonthlyExpiryDate expiryDate={collection.ec_schedule_end_at} />
                            </div>
                          )}
                        </>
                      }

                      {
                        isCreditBasedLegacy(subscription) &&
                        (ownership?.downloads.length < product.variants.length || !ownership) &&
                        <LegacyProductCreditsPurchaseBlock
                          product={product} collection={collection} subscription={subscription} ownedVariants={ownership?.downloads}
                        />
                      }

                      {/** Action buttons */}
                      <div className="">
                        {collection?.library?.id == 1 && (
                          <a
                            href={product.video.link}
                            target="_blank"
                            className={
                              buttonClasses +
                              ' ' +
                              hoverClasses +
                              ' stroke-a-blue text-a-blue border-gray-800 w-full md:w-auto mb-6 md:my-0 flex-1'
                            }>
                            <DownloadIcon className="" /> Download Preview
                          </a>
                        )}
                        <div className="flex flex-col w-full gap-3 pt-3 lg:flex-row">
                          <AddToWishlistButton
                            type="button"
                            product={product}
                            buttonClassName={
                              buttonClasses +
                              ' ' +
                              hoverClasses +
                              ' stroke-a-light-gray text-a-light-gray border-a-light-gray w-full lg:w-auto mb-6 md:my-0 grow'
                            }
                            setIsOpen={setIsOpen}
                            parentCollectionId={collection.id}
                          />
                          <CopyToClipboardButton
                            contentToCopy={location.href}
                            buttonTextBeforeCopy="Copy URL"
                            className={
                              buttonClasses +
                              ' ' +
                              hoverClasses +
                              ' stroke-a-light-gray w-full md:w-auto mb-6 md:my-0 text-a-light-gray border-a-light-gray grow'
                            }
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
};

const mapDispatchToProps = (state: ApplicationState): ProductModalStateProps => ({
  user: state.auth.user as User,
  subscription: state.auth.subscription as SubscriptionDetail,
  authenticated: state.auth.authenticated,
});
export default connect(mapDispatchToProps)(ProductModal);
